import React, { useEffect, useRef, useState } from 'react'
import PlayerLayout from './PlayerLayout'
import { useAudio } from './AudioProvider'

interface Props {
  cardIndex: number
  handleBringBackCard: (index: number) => void
  src: string
  leadActivityId: string
  title: string
}

const AudioPlayer: React.FC<Props> = ({ src, cardIndex, handleBringBackCard, leadActivityId, title }) => {
  const {
    load,
    setIsInlineVisible,
    setVirtualCardIndex,
    toggle,
    seek,
    changeVolume,
    changeSpeed,
    setFallbackMessage,
    isInlineVisible,
    setHandleBringBackCard,
    errorCode,
    fallbackMessage,
    activeId,
    isPlaying,
    currentTime,
    duration: globalDuration,
    buffered,
  } = useAudio()

  const containerRef = useRef<HTMLDivElement>(null)
  const [localDuration, setLocalDuration] = useState<number | null>(null)

  const handleToggle = () => {
    if (!isActive) {
      load(src, leadActivityId, title, { autoplay: true })
    } else {
      toggle()
    }
  }

  useEffect(() => {
    const temp = new Audio(src)
    const handleMeta = () => {
      if (!src) {
        setFallbackMessage('Audio recording is not available')
      }
      setLocalDuration(Math.round(temp.duration))
    }
    temp.addEventListener('loadedmetadata', handleMeta)
    return () => {
      temp.removeEventListener('loadedmetadata', handleMeta)
      temp.src = ''
    }
  }, [src])

  useEffect(() => {
    if (!containerRef.current) return
    if (activeId !== leadActivityId) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInlineVisible(entry.isIntersecting)
      },
      { threshold: 0.1 },
    )

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [setIsInlineVisible, activeId, leadActivityId])

  useEffect(() => {
    if (!isInlineVisible && activeId === leadActivityId) {
      setVirtualCardIndex(cardIndex)
      setHandleBringBackCard(() => handleBringBackCard)
    }
  }, [isInlineVisible])

  const isActive = activeId === leadActivityId

  return (
    <div ref={containerRef}>
      <PlayerLayout
        errorCode={isActive ? errorCode : null}
        fallbackMessage={isActive ? fallbackMessage : ''}
        isPlaying={isActive ? isPlaying : false}
        currentTime={isActive ? currentTime : 0}
        duration={isActive ? globalDuration : localDuration ?? 0}
        buffered={isActive ? buffered : 0}
        src={src}
        toggle={handleToggle}
        seek={seek}
        changeVolume={changeVolume}
        changeSpeed={changeSpeed}
      />
    </div>
  )
}

export default React.memo(AudioPlayer)
